import { useMemo } from 'react';
import { useAppSelector } from '../hook/useAppSelector';
import { resolveGroupBy } from '../util/group-by-helper';
import { GeoClass } from '../interface/response/GeoFiltersResponse';

/**
 * Resolves the effective table grouping configuration based on
 * the selected group-by option, selected devices, and aggregation settings.
 *
 * This hook derives:
 * - the final `groupBy` value used by the table
 * - whether the `device` column should be displayed
 * - whether the `device type` column should be displayed
 *
 * The result is memoized to avoid unnecessary recalculations.
 *
 * @param selectedDevices - List of currently selected devices
 * @returns Object containing:
 * - `resolvedGroupBy` - Final resolved group-by value
 * - `showDevice` - Indicates whether the device column should be shown
 * - `showDeviceType` - Indicates whether the device type column should be shown
 */

export const useResolvedTableState = (selectedDevices: GeoClass[], selectedPaymentTypes: GeoClass[]) => {
  const groupBy = useAppSelector((state) => state.stats.groupBy);
  const shouldSum = useAppSelector((state) => state.globalFilters.shouldSumByDevice);

  const result = useMemo(() => {
    return resolveGroupBy({
      geoGroup: groupBy,
      selectedDevices,
      shouldSum,
      selectedPaymentTypes,
    });
  }, [groupBy, selectedDevices, shouldSum, selectedPaymentTypes]);

  return {
    resolvedGroupBy: result.groupBy,
    showDevice: result.showDevice,
    showDeviceType: result.showDeviceType,
  };
};
